/**
 * Alshallal Pastry Company HR System - Sidebar Navigation
 * Phase 4: Role-aware navigation with RTL support
 * Phase 5: Permission-based UI gating
 * Phase 18.10: Module policy-aware navigation
 * Mobile: Off-canvas drawer with backdrop
 */
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  User,
  FileText, 
  Calendar, 
  ClipboardList,
  ClipboardCheck,
  ClipboardPen,
  Settings,
  Shield,
  ShieldAlert,
  Building2,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  CalendarCheck,
  CalendarClock,
  Upload,
  Network,
  X,
  KeyRound,
  AlertTriangle,
  Database,
  Activity,
  ShieldCheck,
  UserX,
  Fingerprint
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useModulePolicy } from '@/contexts/ModulePolicyContext';
import { usePermission, Permission } from '@/contexts/PermissionContext';
import { cn } from '@/lib/utils';
import { AlshallalLogo } from '@/components/brand/AlshallalLogo';
import { staggerContainer, staggerItem } from '@/lib/motion';

// Navigation items with permission-based visibility
// Phase 5: Changed from role-based to permission-based visibility
const getNavItems = (t, isRTL) => [
  {
    key: 'dashboard',
    moduleKey: 'dashboard',
    label: t('dashboard'),
    icon: LayoutDashboard,
    path: '/dashboard',
    permissions: null, // All authenticated users can see dashboard
  },
  {
    key: 'my-profile',
    moduleKey: 'my_profile',
    label: isRTL ? 'ملفي الشخصي' : 'My Profile',
    icon: User,
    path: '/my-profile',
    permissions: null, // All authenticated users
    // Phase 5: Hide from HR Manager/Super Admin who use admin views
    hideForRoles: ['hr_manager', 'super_admin'],
  },
  {
    key: 'employees',
    moduleKey: 'employees',
    label: t('employees'),
    icon: Building2,
    path: '/employees',
    permissions: [Permission.EMPLOYEE_VIEW_ALL], // HR only
  },
  {
    key: 'documents-company',
    moduleKey: 'documents_company',
    label: isRTL ? 'مستندات الشركة' : 'Company Documents',
    icon: Building2,
    path: '/documents/company',
    permissions: [Permission.COMPANY_DOCUMENT_VIEW, Permission.COMPANY_DOCUMENT_MANAGE],
  },
  {
    key: 'leave',
    moduleKey: 'leave',
    label: isRTL ? 'إجازاتي' : 'My Leave',
    icon: Calendar,
    path: '/leave',
    permissions: [Permission.LEAVE_VIEW_OWN, Permission.LEAVE_REQUEST],
    // Phase 5: Hide from HR Manager/Super Admin who use Leave Admin
    hideForRoles: ['hr_manager', 'super_admin'],
  },
  {
    key: 'leave-approvals',
    moduleKey: 'leave_approvals',
    label: isRTL ? 'موافقات الإجازات' : 'Leave Approvals',
    icon: CalendarCheck,
    path: '/leave/approvals',
    permissions: [Permission.LEAVE_APPROVE_MANAGER, Permission.LEAVE_APPROVE_HR],
  },
  {
    // 2026-11: the direct manager's view of their team's leave. HR roles hold
    // leave_view_all and use Leave Admin (same Arabic title), so it is hidden for them.
    key: 'leave-team',
    moduleKey: 'leave_approvals',
    label: isRTL ? 'إدارة الإجازات' : 'Leave Management',
    icon: CalendarClock,
    path: '/leave/team',
    permissions: [Permission.LEAVE_VIEW_TEAM],
    hideForRoles: ['hr_assistant', 'hr_manager', 'hr_admin', 'super_admin'],
  },
  {
    // 2026-11: department managers report a team member's absence without
    // excuse. The Manager role only - the server refuses every other role.
    key: 'unexcused-absence',
    moduleKey: 'leave_approvals',
    label: isRTL ? 'غياب بدون عذر' : 'Unexcused Absence',
    icon: UserX,
    path: '/leave/unexcused-absence',
    permissions: null,
    requireRole: 'manager',
  },
  {
    // Attendance (fingerprint): HR and branch managers review; employees see their own approved month.
    // The page itself asks /api/attendance/my-access which tabs to show.
    key: 'attendance',
    moduleKey: 'attendance',
    label: isRTL ? 'الحضور والانصراف' : 'Attendance',
    icon: Fingerprint,
    path: '/attendance',
    permissions: null,
    hideForRoles: ['employee'],
  },
  {
    key: 'my-attendance',
    moduleKey: 'attendance',
    label: isRTL ? 'حضوري' : 'My attendance',
    icon: CalendarCheck,
    path: '/attendance/my',
    permissions: null,
    hideForRoles: ['super_admin'],
  },
  {
    key: 'leave-admin',
    moduleKey: 'leave_admin',
    label: isRTL ? 'إدارة الإجازات' : 'Leave Admin',
    icon: CalendarClock,
    path: '/leave/admin',
    permissions: [Permission.LEAVE_VIEW_ALL, Permission.LEAVE_TYPES_MANAGE, Permission.LEAVE_BALANCE_ADJUST],
  },
  {
    key: 'requests',
    moduleKey: 'requests',
    label: isRTL ? 'طلباتي' : 'My Requests',
    icon: ClipboardList,
    path: '/requests',
    permissions: [Permission.REQUEST_VIEW_OWN, Permission.REQUEST_CREATE],
    // Phase 5: Hide from HR who use Requests Admin
    hideForRoles: ['hr_manager', 'hr_admin', 'super_admin'],
  },
  {
    key: 'requests-approvals',
    moduleKey: 'requests_approvals',
    label: isRTL ? 'موافقات الطلبات' : 'Request Approvals',
    icon: ClipboardCheck,
    path: '/requests/approvals',
    permissions: [Permission.REQUEST_APPROVE_MANAGER, Permission.REQUEST_APPROVE_HR],
  },
  {
    key: 'requests-admin',
    moduleKey: 'requests_admin',
    label: isRTL ? 'إدارة الطلبات' : 'Requests Admin',
    icon: ClipboardPen,
    path: '/requests/admin',
    permissions: [Permission.REQUEST_VIEW_ALL],
  },
  {
    key: 'letters',
    moduleKey: 'letters',
    label: isRTL ? 'خطاباتي' : 'My Letters',
    icon: FileText,
    path: '/letters',
    permissions: [Permission.LETTERS_VIEW_OWN, Permission.LETTERS_REQUEST],
    // Phase 5: Hide from HR who see Letters Management
    hideForRoles: ['hr_manager', 'hr_admin', 'super_admin'],
  },
  {
    key: 'letters-hr',
    moduleKey: 'letters_hr',
    label: isRTL ? 'إدارة الخطابات' : 'Letters Management',
    icon: FileText,
    path: '/letters/hr',
    permissions: [Permission.LETTERS_VIEW_ALL],
  },
  {
    key: 'import',
    moduleKey: 'import',
    label: isRTL ? 'استيراد الموظفين' : 'Import Employees',
    icon: Upload,
    path: '/import',
    permissions: [Permission.EMPLOYEE_BULK_IMPORT],
  },
  {
    key: 'org-chart',
    moduleKey: 'org_chart',
    label: isRTL ? 'الهيكل التنظيمي' : 'Org Chart',
    icon: Network,
    path: '/org-chart',
    permissions: [Permission.EMPLOYEE_VIEW_ALL],
  },
  {
    key: 'pending-updates',
    moduleKey: 'admin_console',
    label: isRTL ? 'تحديثات معلقة' : 'Pending Updates',
    icon: ShieldAlert,
    path: '/admin/pending-updates',
    // the backend gate (routes/pending_updates.py): Super Admin and HR Manager only
    permissions: null,
    allowRoles: ['super_admin', 'hr_manager'],
  },
  {
    key: 'qc-violations',
    moduleKey: null,
    label: isRTL ? 'مخالفات ضبط الجودة' : 'QC Violations',
    icon: AlertTriangle,
    path: '/qc-violations',
    // Access is controlled at runtime by /api/qc-violations/my-access, which
    // reads the qc_access_settings singleton. Super Admin can add/remove
    // roles in the QC hub → Access Control tab. Backend also 403s directly.
    permissions: null,
  },
  {
    key: 'admin-maintenance',
    moduleKey: null,
    label: isRTL ? 'صيانة الإدارة' : 'Admin Maintenance',
    icon: Database,
    path: '/admin/maintenance',
    permissions: null,
    requireRole: 'super_admin',
  },
  {
    key: 'authority',
    moduleKey: null, // No module policy, permission-based only
    label: isRTL ? 'صلاحيات الموارد البشرية' : 'HR Authority',
    icon: KeyRound,
    path: '/authority',
    permissions: [Permission.AUTHORITY_DASHBOARD_VIEW],
  },
  {
    // 2026-06: HR Manager and HR Admin no longer see the System shell, but they
    // still hold the audit log. These two entries surface the pages they keep,
    // and hide themselves for anyone who reaches them through System anyway.
    key: 'audit-logs',
    moduleKey: 'audit',
    label: isRTL ? 'سجلات التدقيق' : 'Audit Logs',
    icon: Activity,
    path: '/system/audit',
    permissions: [Permission.AUDIT_VIEW_ALL],
    hideWhenSystemShell: true,
  },
  {
    key: 'account-admin',
    moduleKey: 'admin_console',
    label: isRTL ? 'إدارة الحسابات' : 'Account Administration',
    icon: ShieldCheck,
    path: '/system/admin',
    permissions: [Permission.USER_VIEW_ALL],
    hideWhenSystemShell: true,
  },
  {
    key: 'system',
    moduleKey: 'system',
    label: t('system'),
    icon: Shield,
    path: '/system',
    permissions: [Permission.AUDIT_VIEW_ALL, Permission.SYSTEM_SETTINGS],
  },
  {
    key: 'settings',
    moduleKey: null, // Settings visibility is permission-based, subpages use module policy
    label: t('settings'),
    icon: Settings,
    path: '/settings',
    permissions: [Permission.SYSTEM_SETTINGS, Permission.USER_VIEW_ALL, Permission.COMPANY_DOCUMENT_MANAGE],
  }
];

export const Sidebar = ({ 
  collapsed, 
  onToggle, 
  isMobile = false, 
  mobileOpen = false,
  onMobileClose 
}) => {
  const { user, token } = useAuth();
  const { t, isRTL } = useLanguage();
  const { isModuleEnabled } = useModulePolicy();
  const { hasAnyPermission, permissionsLoading } = usePermission();
  const location = useLocation();
  const sidebarRef = useRef(null);

  // QC hub access: source of truth is the backend qc_access_settings toggle.
  // We fetch on mount + on user change so a Super Admin flipping the toggle
  // takes effect on the next login/reload.
  // Starts as "not yet known" rather than `true`: an optimistic default flashed
  // the QC entry into the menu of somebody who had just entered as Employee,
  // for as long as the answer took to arrive.
  const [qcAllowed, setQcAllowed] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user || !token) return;
    (async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/qc-violations/my-access`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setQcAllowed(!!r.data?.allowed);
      } catch {
        if (!cancelled) setQcAllowed(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  const navItems = getNavItems(t, isRTL);
  
  // Filter items based on permissions AND module policy
  // Phase 5: Changed from role-based to permission-based filtering
  const visibleItems = navItems.filter(item => {
    if (!user) return false;
    
    // Check hideForRoles first (for items that should be hidden from certain roles)
    if (item.hideForRoles && item.hideForRoles.includes(user.role)) {
      return false;
    }

    // Role whitelist (rare — for super-admin-only tools like admin maintenance).
    if (item.requireRole && user.role !== item.requireRole) {
      return false;
    }
    if (item.allowRoles && !item.allowRoles.includes(user.role)) {
      return false;
    }

    // QC hub — backend access toggle is the source of truth. If disabled for
    // this role, hide the nav entry (and the route itself will 403).
    if (item.key === 'qc-violations' && qcAllowed !== true) {
      return false;
    }
    
    // Check permission-based visibility
    // If permissions is null, visible to all authenticated users
    if (item.permissions !== null && !hasAnyPermission(item.permissions)) {
      return false;
    }
    
    // 2026-06: entries that duplicate a page inside the System shell hide
    // themselves for anyone who has the shell.
    if (item.hideWhenSystemShell && isModuleEnabled('system')) {
      return false;
    }
    
    // Then check module policy (if moduleKey is set)
    if (item.moduleKey && !isModuleEnabled(item.moduleKey)) {
      return false;
    }
    
    return true;
  });

  // Focus trap for mobile drawer
  useEffect(() => {
    if (isMobile && mobileOpen && sidebarRef.current) {
      const firstFocusable = sidebarRef.current.querySelector('button, a');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  }, [isMobile, mobileOpen]);

  const CollapseIcon = isRTL 
    ? (collapsed ? ChevronLeft : ChevronRight)
    : (collapsed ? ChevronRight : ChevronLeft);

  // Mobile drawer classes - z-50 to be above backdrop (z-45)
  const mobileDrawerClasses = cn(
    "fixed top-0 h-screen bg-card z-50 transition-transform duration-300 ease-in-out shadow-2xl",
    "w-[280px] max-w-[85vw]",
    isRTL ? "right-0" : "left-0",
    // Transform for open/close
    mobileOpen 
      ? "translate-x-0" 
      : (isRTL ? "translate-x-full" : "-translate-x-full")
  );

  // Desktop sidebar classes
  const desktopClasses = cn(
    "fixed top-0 h-screen bg-card border-e border-border z-40 transition-all duration-300",
    collapsed ? "w-16" : "w-64",
    isRTL ? "right-0" : "left-0"
  );

  // Handle nav item click on mobile
  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Show loading placeholder while permissions load
  if (permissionsLoading) {
    return (
      <aside 
        className={isMobile ? mobileDrawerClasses : desktopClasses}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center justify-center px-4 border-b border-border">
          <div className="animate-pulse bg-brand-beige-60 h-8 w-24 rounded"></div>
        </div>
        <nav className="p-3 space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="animate-pulse bg-brand-beige-60 h-10 rounded-md"></div>
          ))}
        </nav>
      </aside>
    );
  }

  return (
    <aside 
      ref={sidebarRef}
      className={isMobile ? mobileDrawerClasses : desktopClasses}
      data-testid="sidebar"
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Header with Logo and Close/Toggle button */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border bg-card">
        {/* Mobile: Always show logo, Desktop: show when not collapsed */}
        {(isMobile || !collapsed) && (
          <div className="flex items-center gap-2.5 min-w-0">
            <AlshallalLogo size="sm" animated={true} />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-bold text-brand-black text-sm truncate" data-testid="sidebar-logo">
                {isRTL ? 'معجنات الشلال' : 'Alshallal Pastry'}
              </span>
              <span className="text-[10px] font-medium text-brand-brown truncate">
                {isRTL ? 'للموارد البشرية' : 'Human Resources'}
              </span>
            </div>
          </div>
        )}
        {/* Collapsed: Show only logo */}
        {!isMobile && collapsed && (
          <AlshallalLogo size="sm" animated={true} className="mx-auto" />
        )}
        
        {/* Mobile: Close button, Desktop: Collapse toggle */}
        {isMobile ? (
          <button
            onClick={onMobileClose}
            className="p-2 rounded-md hover:bg-brand-beige-60 transition-colors ms-auto"
            data-testid="sidebar-close"
            aria-label="Close menu"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        ) : (
          <button
            onClick={onToggle}
            className={cn(
              "p-2 rounded-md hover:bg-brand-beige-60 transition-colors",
              collapsed && "mx-auto"
            )}
            data-testid="sidebar-toggle"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <CollapseIcon className="h-5 w-5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <motion.nav
        className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]"
        variants={staggerContainer(0.025)}
        initial="hidden"
        animate="visible"
      >
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
                          location.pathname.startsWith(item.path + '/');
          
          return (
            <motion.div key={item.key} variants={staggerItem}>
              <NavLink
                to={item.path}
                onClick={handleNavClick}
                data-testid={`nav-${item.key}`}
                className={cn(
                  "group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                  isActive 
                    ? "bg-brand-black text-brand-beige shadow-brand-md" 
                    : "text-foreground/75 hover:bg-brand-beige-60 hover:text-brand-black",
                  !isMobile && collapsed && "justify-center"
                )}
                title={!isMobile && collapsed ? item.label : undefined}
              >
                {/* Active indicator stripe */}
                {isActive && (
                  <motion.span
                    layoutId="sidebar-active-stripe"
                    className={cn(
                      "absolute top-1.5 bottom-1.5 w-1 rounded-full bg-brand-orange",
                      isRTL ? "right-0" : "left-0"
                    )}
                    transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                  />
                )}
                <Icon className={cn(
                  "h-5 w-5 flex-shrink-0 transition-transform duration-200",
                  "group-hover:scale-110",
                  isActive && "text-brand-orange"
                )} />
                {(isMobile || !collapsed) && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
              </NavLink>
            </motion.div>
          );
        })}
      </motion.nav>
    </aside>
  );
};

export default Sidebar;
